import React, { useState } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { motion, useInView } from 'framer-motion';
import Icon from '../common/Icon';
import ScatterText from '../common/ScatterText';
import './Contact.css';
import './contact-responsive.css';

const Contact: React.FC = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [showAlert, setShowAlert] = useState(false);

  // Formspree integration
  const [state, handleSubmit] = useForm("xeoljapa");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Show alert on success
  React.useEffect(() => {
    if (state.succeeded) {
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }
  }, [state.succeeded]);

  return (
    <section id="contact" className="contact-section">
      <Container>
        <motion.div 
          className="section-title text-center mb-5"
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ 
            type: "spring", 
            stiffness: 100, 
            damping: 15, 
            duration: 0.7 
          }}
        >
          <ScatterText text="Get In Touch" className="section-title-scatter" />
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Feel free to contact me if you have any questions or want to collaborate
          </motion.p>
        </motion.div>
        <motion.div 
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <Row>
            <Col xs={12}>
              {/* Social links at the top */}
              <motion.div 
                className="social-links-container text-center mb-4"
                variants={itemVariants}
              >
                <motion.div 
                  className="d-flex justify-content-center"
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ staggerChildren: 0.1, delayChildren: 0.2 }}
                >
                  {[
                    { url: "https://www.linkedin.com/in/muhammadrahimusman/", icon: "FaLinkedin", color: "#0077B5" },
                    { url: "https://github.com/Rebelhere", icon: "FaGithub", color: "#333" },
                    { url: "https://www.instagram.com/rahim.usman.77/", icon: "FaInstagram", color: "#E1306C" },
                    { url: "mailto:muhammadrahimusman@gmail.com", icon: "FaEnvelope", color: "#D44638" },
                    { url: "https://x.com/ra55681", icon: "FaTwitter", color: "#1DA1F2" }
                  ].map((social, index) => (
                    <motion.a 
                      key={index}
                      href={social.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                      transition={{ 
                        delay: 0.2 + index * 0.1,
                        type: "spring",
                        stiffness: 260,
                        damping: 20
                      }}
                      whileHover={{ 
                        scale: 1.2, 
                        y: -5,
                        backgroundColor: social.color,
                        color: '#fff',
                        boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
                      }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Icon icon={social.icon} />
                    </motion.a>
                  ))}
                </motion.div>
              </motion.div>
            </Col>
            <Col lg={10} md={10} className="mx-auto">
              <motion.div 
                className="contact-form p-4 rounded shadow-sm"
                variants={itemVariants}
                whileHover={{ 
                  boxShadow: '0 15px 30px rgba(0, 0, 0, 0.15)',
                  transition: { duration: 0.3 }
                }}
              >
                <motion.h3
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
                >
                  Send Me a Message
                </motion.h3>
                {showAlert && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  >
                    <Alert 
                      variant="success" 
                      onClose={() => setShowAlert(false)} 
                      dismissible
                      className="success-alert"
                    >
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ 
                          opacity: [0, 1, 0, 1],
                          transition: { duration: 1.5 }
                        }}
                      >
                        Your message has been sent successfully!
                      </motion.div>
                    </Alert>
                  </motion.div>
                )}
                <Form onSubmit={handleSubmit} className="w-100">
                  <Row>
                    <Col md={6} className="mb-3">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                      >
                        <Form.Group controlId="name">
                          <Form.Label>Name</Form.Label>
                          <Form.Control 
                            id="name"
                            type="text" 
                            name="name" 
                            placeholder="Your Name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="form-input bg-white dark-bg-dark text-dark dark-text-light border border-secondary rounded"
                          />
                        </Form.Group>
                      </motion.div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                      >
                        <Form.Group controlId="email">
                          <Form.Label>Email</Form.Label>
                          <Form.Control 
                            id="email"
                            type="email" 
                            name="email" 
                            placeholder="Your Email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="form-input bg-white dark-bg-dark text-dark dark-text-light border border-secondary rounded"
                          />
                          <ValidationError 
                            prefix="Email" 
                            field="email"
                            errors={state.errors}
                          />
                        </Form.Group>
                      </motion.div>
                    </Col>
                  </Row>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <Form.Group className="mb-3" controlId="subject">
                      <Form.Label>Subject</Form.Label>
                      <Form.Control 
                        id="subject"
                        type="text" 
                        name="subject" 
                        placeholder="Subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="form-input bg-white dark-bg-dark text-dark dark-text-light border border-secondary rounded"
                      />
                    </Form.Group>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                  >
                    <Form.Group className="mb-4" controlId="message">
                      <Form.Label>Message</Form.Label>
                      <Form.Control 
                        id="message"
                        as="textarea" 
                        name="message" 
                        rows={5} 
                        placeholder="Your Message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="form-input bg-white dark-bg-dark text-dark dark-text-light border border-secondary rounded"
                      />
                      <ValidationError 
                        prefix="Message" 
                        field="message"
                        errors={state.errors}
                      />
                    </Form.Group>
                  </motion.div>

                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: 0.7, duration: 0.5 }}
                    whileHover={{ 
                      scale: 1.05,
                      transition: { 
                        type: "spring", 
                        stiffness: 400, 
                        damping: 10 
                      }
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button 
                      variant="primary" 
                      type="submit" 
                      className="submit-btn w-100 py-2 mt-2 dark-bg-accent dark-text-light"
                      disabled={state.submitting}
                      style={{ fontWeight: 600, fontSize: '1.1rem', letterSpacing: '0.5px' }}
                    >
                      <motion.span
                        initial={{ opacity: 1 }}
                        whileHover={{
                          opacity: [1, 0.8, 1],
                          transition: { repeat: Infinity, duration: 1 }
                        }}
                      >
                        Send Message
                      </motion.span>
                    </Button>
                  </motion.div>
                </Form>
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </section>
  );
};

export default Contact;
